import { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  Clock, Package, Truck, CheckCheck, XCircle, AlertCircle,
  ShoppingCart, TrendingUp, CheckCircle, ArrowRight, RefreshCw,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const STATUS_CFG = {
  pending:    { label: 'Da confermare', short: 'Da conf.',  bg: 'bg-yellow-50', border: 'border-yellow-300', text: 'text-yellow-800', dot: 'bg-yellow-400', icon: Clock,         urgent: true  },
  to_prepare: { label: 'Da preparare',  short: 'Da prep.',  bg: 'bg-blue-50',   border: 'border-blue-300',   text: 'text-blue-800',   dot: 'bg-blue-500',   icon: CheckCircle,   urgent: true  },
  preparing:  { label: 'In prep.',      short: 'In prep.',  bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-800', dot: 'bg-purple-400', icon: Package,       urgent: false },
  ready:      { label: 'Pronto',        short: 'Pronto',    bg: 'bg-teal-50',   border: 'border-teal-200',   text: 'text-teal-800',   dot: 'bg-teal-400',   icon: Package,       urgent: false },
  shipped:    { label: 'Spedito',       short: 'Spedito',   bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-800', dot: 'bg-indigo-400', icon: Truck,         urgent: false },
  completed:  { label: 'Completato',    short: 'Evaso',     bg: 'bg-green-50',  border: 'border-green-200',  text: 'text-green-800',  dot: 'bg-green-400',  icon: CheckCheck,    urgent: false },
  cancelled:  { label: 'Annullato',     short: 'Annullato', bg: 'bg-red-50',    border: 'border-red-200',    text: 'text-red-800',    dot: 'bg-red-400',    icon: XCircle,       urgent: false },
};

const PIPELINE = ['pending', 'to_prepare', 'preparing', 'ready', 'shipped', 'completed'];

function fmtTime(d) {
  if (!d) return '—';
  const diff = Date.now() - new Date(d).getTime();
  const m = Math.floor(diff / 60000);
  const h = Math.floor(diff / 3600000);
  const g = Math.floor(diff / 86400000);
  if (m < 1) return 'adesso';
  if (m < 60) return `${m}m fa`;
  if (h < 24) return `${h}h fa`;
  return `${g}g fa`;
}

function fmtPrice(p) {
  if (!p || Number(p) === 0) return null;
  return `€ ${Number(p).toFixed(2).replace('.', ',')}`;
}

function isToday(d) {
  if (!d) return false;
  const t = new Date(d);
  const n = new Date();
  return t.getDate() === n.getDate() && t.getMonth() === n.getMonth() && t.getFullYear() === n.getFullYear();
}

function parseItems(items) {
  if (!items) return [];
  if (Array.isArray(items)) return items;
  try { const p = JSON.parse(items); return Array.isArray(p) ? p : []; } catch { return []; }
}

export default function AdminDashboard() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (soft = false) => {
    if (soft) setRefreshing(true); else setLoading(true);
    try {
      const res = await axios.get(`${API}/orders`);
      setOrders(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Errore caricamento ordini:', e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  // ── Calcoli ───────────────────────────────────────────────────────────────
  const byStatus = {};
  [...PIPELINE, 'cancelled'].forEach(s => { byStatus[s] = orders.filter(o => o.status === s); });

  const urgentCount = (byStatus.pending?.length ?? 0) + (byStatus.to_prepare?.length ?? 0);
  const todayCount  = orders.filter(o => isToday(o.created_at)).length;
  const revenue     = orders.filter(o => o.status === 'completed').reduce((s, o) => s + (Number(o.total) || 0), 0);
  const recentOrders = [...orders].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 7);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="min-w-0 w-full space-y-4">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-xl font-bold text-gray-900">Gestione Ordini</h2>
          {!loading && urgentCount > 0 && (
            <p className="font-body text-xs text-orange-600 flex items-center gap-1 mt-0.5">
              <AlertCircle className="w-3 h-3" />
              {urgentCount} {urgentCount === 1 ? 'ordine richiede' : 'ordini richiedono'} attenzione immediata
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => load(true)}
            className={`p-1.5 text-gray-400 hover:text-primary transition-colors rounded-lg hover:bg-gray-100 ${refreshing ? 'animate-spin' : ''}`}
            title="Aggiorna"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <Link
            to="/admin/ordini"
            className="flex items-center gap-1.5 text-xs font-body font-semibold text-primary hover:text-primary-dark transition-colors bg-primary/5 hover:bg-primary/10 px-3 py-1.5 rounded-lg"
          >
            Tutti gli ordini
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            {[...Array(6)].map((_, i) => <div key={i} className="bg-white rounded-xl h-[72px] animate-pulse border border-gray-100" />)}
          </div>
          <div className="grid grid-cols-3 gap-2">
            {[...Array(3)].map((_, i) => <div key={i} className="bg-white rounded-xl h-16 animate-pulse border border-gray-100" />)}
          </div>
          <div className="bg-white rounded-xl h-52 animate-pulse border border-gray-100" />
        </div>
      ) : (
        <>
          {/* ── Pipeline: un blocco per stato ── */}
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            {PIPELINE.map(status => {
              const cfg = STATUS_CFG[status];
              const count = byStatus[status]?.length ?? 0;
              const isUrgentActive = cfg.urgent && count > 0;
              const Icon = cfg.icon;
              return (
                <Link key={status} to="/admin/ordini">
                  <div className={`rounded-xl p-3 border-2 transition-all hover:shadow-md cursor-pointer h-[72px] flex flex-col justify-between ${
                    isUrgentActive
                      ? `${cfg.bg} ${cfg.border}`
                      : 'bg-white border-gray-100 hover:border-primary/20'
                  }`}>
                    <div className="flex items-center justify-between">
                      <Icon className={`w-3.5 h-3.5 ${isUrgentActive ? cfg.text : count === 0 ? 'text-gray-200' : 'text-gray-400'}`} />
                      {isUrgentActive && <span className={`w-2 h-2 rounded-full animate-pulse ${cfg.dot}`} />}
                    </div>
                    <div>
                      <p className={`font-heading text-xl font-bold leading-none ${
                        isUrgentActive ? cfg.text : count === 0 ? 'text-gray-200' : 'text-gray-800'
                      }`}>{count}</p>
                      <p className={`font-body text-[10px] truncate mt-0.5 ${
                        isUrgentActive ? cfg.text : 'text-gray-400'
                      }`}>{cfg.short}</p>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>

          {/* ── KPI ── */}
          <div className="grid grid-cols-3 gap-2">
            {[
              { label: 'Totale ordini', value: orders.length, sub: `${todayCount} oggi`, icon: ShoppingCart, color: 'text-primary' },
              { label: 'Ordini evasi',  value: byStatus.completed?.length ?? 0, sub: 'completati', icon: CheckCheck, color: 'text-green-600' },
              { label: 'Fatturato',     value: revenue > 0 ? fmtPrice(revenue) : '—', sub: 'da completati', icon: TrendingUp, color: 'text-indigo-600' },
            ].map(kpi => {
              const Icon = kpi.icon;
              return (
                <div key={kpi.label} className="bg-white rounded-xl p-3 border border-gray-100 flex items-center gap-2.5">
                  <div className="w-8 h-8 bg-gray-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon className={`w-4 h-4 ${kpi.color}`} />
                  </div>
                  <div className="min-w-0">
                    <p className="font-heading text-base font-bold text-gray-900 leading-tight">{kpi.value}</p>
                    <p className="font-body text-[10px] text-gray-500 leading-tight">{kpi.label}</p>
                    <p className="font-body text-[10px] text-gray-300 leading-tight">{kpi.sub}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* ── Ultimi ordini ── */}
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-50">
              <h3 className="font-heading text-sm font-bold text-gray-900">Ultimi ordini ricevuti</h3>
              <Link to="/admin/ordini" className="font-body text-xs text-primary hover:underline flex items-center gap-1">
                Vedi tutti <ArrowRight className="w-3 h-3" />
              </Link>
            </div>

            {recentOrders.length === 0 ? (
              <div className="py-10 text-center">
                <ShoppingCart className="w-10 h-10 text-gray-200 mx-auto mb-2" />
                <p className="font-body text-sm text-gray-400">Nessun ordine ancora</p>
                <p className="font-body text-xs text-gray-300 mt-1">Gli ordini dal negozio online appariranno qui</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-50">
                {recentOrders.map(order => {
                  const cfg = STATUS_CFG[order.status] || STATUS_CFG.pending;
                  const items = parseItems(order.items);
                  const itemCount = items.reduce((s, i) => s + (Number(i.quantity) || 1), 0);
                  const firstItem = items[0]?.product_name || '—';
                  const isNew = isToday(order.created_at);
                  return (
                    <Link
                      key={order.id}
                      to={`/admin/ordini/${order.id}`}
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors group"
                    >
                      {/* Status dot */}
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${cfg.dot}`} />

                      {/* Customer + items */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="font-body text-sm font-semibold text-gray-800 truncate">{order.customer_name}</p>
                          {isNew && <span className="text-[9px] font-bold bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full flex-shrink-0">NUOVO</span>}
                        </div>
                        <p className="font-body text-xs text-gray-400 truncate">
                          {firstItem}{items.length > 1 ? ` +${items.length - 1}` : ''}
                          {' · '}{itemCount} {itemCount === 1 ? 'articolo' : 'articoli'}
                          {order.total > 0 ? ` · ${fmtPrice(order.total)}` : ''}
                        </p>
                      </div>

                      {/* Status badge + time */}
                      <div className="text-right flex-shrink-0 flex flex-col items-end gap-0.5">
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.text}`}>
                          {cfg.short}
                        </span>
                        <span className="font-body text-[10px] text-gray-300">{fmtTime(order.created_at)}</span>
                      </div>

                      <ArrowRight className="w-3.5 h-3.5 text-gray-200 group-hover:text-primary transition-colors flex-shrink-0" />
                    </Link>
                  );
                })}
              </div>
            )}
          </div>

          {/* ── Annullati (compatto, solo se presenti) ── */}
          {byStatus.cancelled?.length > 0 && (
            <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-100 rounded-xl">
              <XCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
              <p className="font-body text-xs text-red-600">
                {byStatus.cancelled.length} {byStatus.cancelled.length === 1 ? 'ordine annullato' : 'ordini annullati'}
              </p>
              <Link to="/admin/ordini" className="ml-auto text-xs text-red-500 hover:underline font-body">Vedi →</Link>
            </div>
          )}
        </>
      )}
    </div>
  );
}
